/**
 * Mirrors of the C# Logistics › Inventory Audit DTO records, field for field.
 *
 * Its own file rather than more of `stockTypes.ts`, on the reason that file gives for splitting
 * from `inventoryTypes.ts`: a module that would double the length of a file nobody could then
 * see the shape of gets its own.
 */

import type { ListQuery } from '../../administration/users/userTypes';
import type { StockAdjustmentDirection } from './stockTypes';

/**
 * Where an audit has got to. Enums serialize as int, so the numbers are the contract.
 *
 * <b>3 `approved` and 5 `rejected` are RESERVED and nothing writes them.</b> The source routes a
 * new audit through a central approval engine; this product has no approvals module, so there is
 * nothing that could produce either state. The numbers are the source's own so that adding
 * approvals later writes those values and renumbers no stored row — the reserved-enum call
 * `ISSUANCE_TYPE` and `InventoryItemKind.RealProperty` already take.
 *
 * <b>The facet still emits both</b>, reading zero. A value that is counted and never offered is
 * the defect both lease alerts shipped with on Stock Levels, and dropping them here would change
 * the strip's shape the day approvals land.
 */
export const INVENTORY_AUDIT_STATUS = {
  draft: 0,
  completed: 1,
  cancelled: 2,
  approved: 3,
  inProgress: 4,
  rejected: 5,
} as const;

export type InventoryAuditStatus =
  (typeof INVENTORY_AUDIT_STATUS)[keyof typeof INVENTORY_AUDIT_STATUS];

/*
  THERE IS DELIBERATELY NO LABEL MAP HERE, and the absence is worth stating because every
  sibling module has one.

  `ISSUANCE_TYPE_LABEL` exists because that row carries a raw enum its list has to name. This
  one does not: `statusLabel` is resolved server-side from `InventoryAuditStatuses.Label` and
  rides on every row, and the `stage` facet's values arrive from the server carrying their own
  labels — so a client map would have no consumer at all, which is the "collected, stored and
  never read" failure in its cheapest form. One vocabulary, defined once, on the server.

  A surface that genuinely has no row to read a label from should take one from the server
  rather than reintroducing a second copy here.
*/

/** Whether the audit has finished — nothing about it can be changed again. */
export function isAuditConcluded(status: InventoryAuditStatus): boolean {
  return (
    status === INVENTORY_AUDIT_STATUS.completed ||
    status === INVENTORY_AUDIT_STATUS.cancelled ||
    status === INVENTORY_AUDIT_STATUS.rejected
  );
}

/**
 * The eight reasons the source offers for a variance.
 *
 * <b>SUGGESTIONS, not a catalog.</b> `<Select allowCustom>` with no create handler commits
 * whatever is typed — there is no vocabulary table behind this and nothing to create. A picker
 * that opened empty would make every reader invent their own wording one row at a time, which is
 * how five spellings of one reason arrive.
 *
 * They are the source's own words, and `<Select>` sorts them alphabetically like every other
 * picker in this product.
 */
export const INVENTORY_AUDIT_VARIANCE_REASONS: readonly string[] = [
  'Counting error',
  'Damage',
  'Data entry error',
  'Expired items removed',
  'Missing receiving transaction',
  'Theft',
  'Wrong issuance',
  'Wrong warehouse transfer',
];

export interface InventoryAuditFacilityRef {
  storageFacilityId: string;
  storageFacilityName: string;
}

export interface InventoryAuditListItem {
  id: string;
  auditNumber: string;
  auditStartDate: string;
  auditEndDate: string;
  /** Resolved through the identity store. Never a name on this row. */
  auditedByPersonnelId: string | null;
  /** Null until the audit starts — the baseline is captured then, not at create. */
  snapshotAsOf: string | null;
  startedAt: string | null;
  status: InventoryAuditStatus;
  statusLabel: string;
  completedAt: string | null;
  notes: string | null;
  facilities: InventoryAuditFacilityRef[];
  totalItems: number;
  countedItems: number;
  itemsWithVariance: number;
  netVariance: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface InventoryAuditLineFacilityDetail {
  id: string;
  storageFacilityId: string;
  storageFacilityName: string;
  snapshotQuantity: number;
  actualQuantity: number | null;
}

export interface InventoryAuditLineDetail {
  id: string;
  inventoryItemId: string;
  /** Joined off the catalog on every read — never a stored copy. */
  itemSku: string;
  itemName: string;
  unitOfMeasureName: string;
  inventoryCategoryId: string | null;
  inventoryCategoryName: string | null;
  snapshotQuantity: number;
  actualQuantity: number | null;
  varianceQuantity: number | null;
  varianceReason: string | null;
  accountablePersonnelId: string | null;
  /** SIGNED. The form asks a direction and a magnitude; the server converts once. */
  adjustmentQuantity: number | null;
  isActive: boolean;
  facilityBreakdown: InventoryAuditLineFacilityDetail[];
}

export interface InventoryAuditDetail {
  id: string;
  auditNumber: string;
  auditStartDate: string;
  auditEndDate: string;
  auditedByPersonnelId: string | null;
  snapshotAsOf: string | null;
  startedAt: string | null;
  status: InventoryAuditStatus;
  statusLabel: string;
  completedAt: string | null;
  notes: string | null;
  cancellationReason: string | null;
  facilities: InventoryAuditFacilityRef[];
  totalItems: number;
  countedItems: number;
  itemsWithVariance: number;
  netVariance: number;
  lines: InventoryAuditLineDetail[];
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface InventoryAuditListQuery extends ListQuery {
  /** The lifecycle. `status` is reserved for the row's own live-or-deleted state. */
  stage?: string;
  facility?: string;
}

export interface CreateInventoryAuditRequest {
  auditStartDate: string;
  auditEndDate: string;
  auditedByPersonnelId: string | null;
  notes: string | null;
  storageFacilityIds: string[];
}

export interface StartInventoryAuditRequest {
  confirmEarlyStart: boolean;
}

export interface RecordInventoryAuditCountFacility {
  storageFacilityId: string;
  actualQuantity: number | null;
}

export interface RecordInventoryAuditCountRequest {
  actualQuantity: number | null;
  varianceReason: string | null;
  accountablePersonnelId: string | null;
  /** Both halves or neither — the server refuses one without the other. */
  adjustmentDirection: StockAdjustmentDirection | null;
  /** UNSIGNED. The direction beside it carries the sign. */
  adjustmentQuantity: number | null;
  facilities: RecordInventoryAuditCountFacility[] | null;
}

export interface CancelInventoryAuditRequest {
  reason: string;
}
